(() => {
  const navigationSelector = '[data-product-anchor-nav]';

  class ProductAnchorScrollSpy {
    constructor(root) {
      if (root.dataset.scrollSpyInitialized === 'true') return;

      root.dataset.scrollSpyInitialized = 'true';
      this.root = root;
      this.section = root.closest('.shopify-section--product-anchor-nav');
      this.track = root.querySelector('.product-anchor-nav__track');
      this.links = Array.from(root.querySelectorAll('[data-anchor-id]'));
      this.activeLink = null;
      this.updateFrame = null;
      this.refreshFrame = null;

      this.handleScroll = () => this.queueUpdate();
      this.handleResize = () => this.queueRefresh();

      this.links.forEach((link) => {
        link.addEventListener('click', (event) => this.handleClick(event, link));
      });

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });

      this.observeProductSection();
      this.refresh();
      requestAnimationFrame(() => this.refresh());
    }

    findTarget(anchorId) {
      const directTarget = document.getElementById(anchorId);
      if (directTarget) return directTarget;

      const sectionTarget = Array.from(document.querySelectorAll('[id]')).find((element) => element.id.endsWith(`__${anchorId}`));
      return sectionTarget;
    }

    refresh() {
      this.items = this.links
        .map((link) => ({ link, target: this.findTarget(link.dataset.anchorId) }))
        .filter(({ link, target }) => {
          const targetIsVisible = Boolean(target && target.isConnected && target.getClientRects().length);
          link.hidden = !targetIsVisible;
          return targetIsVisible;
        });

      this.update();
    }

    observeProductSection() {
      this.productObserver?.disconnect();

      const productSection = document.querySelector('.shopify-section--main-product');
      if (!productSection) return;

      this.productObserver = new MutationObserver(() => this.queueRefresh());
      this.productObserver.observe(productSection, { childList: true, subtree: true });
    }

    queueRefresh() {
      if (this.refreshFrame) return;

      this.refreshFrame = requestAnimationFrame(() => {
        this.refreshFrame = null;
        this.observeProductSection();
        this.refresh();
      });
    }

    queueUpdate() {
      if (this.updateFrame) return;

      this.updateFrame = requestAnimationFrame(() => {
        this.updateFrame = null;
        this.update();
      });
    }

    getStickyOffset() {
      return this.section ? parseFloat(getComputedStyle(this.section).insetBlockStart) || 0 : 0;
    }

    getProbePosition() {
      const contentViewportHeight = Math.max(0, window.innerHeight - this.getStickyOffset() - this.root.offsetHeight);
      return Math.min(window.innerHeight - 1, this.getStickyOffset() + this.root.offsetHeight + Math.min(contentViewportHeight * 0.2, 140));
    }

    update() {
      if (!this.items?.length) {
        this.setActive(null);
        return;
      }

      if (this.items.some(({ target }) => !target.isConnected)) {
        this.queueRefresh();
        return;
      }

      const probePosition = this.getProbePosition();
      const containingItems = this.items.filter(({ target }) => {
        const rect = target.getBoundingClientRect();
        return rect.top <= probePosition && rect.bottom > probePosition;
      });

      let currentItem;

      if (containingItems.length) {
        currentItem = containingItems.reduce((smallestItem, item) => {
          const smallestHeight = smallestItem.target.getBoundingClientRect().height;
          const itemHeight = item.target.getBoundingClientRect().height;
          return itemHeight < smallestHeight ? item : smallestItem;
        });
      } else {
        currentItem = this.items
          .filter(({ target }) => target.getBoundingClientRect().top <= probePosition)
          .reduce((closestItem, item) => {
            if (!closestItem) return item;
            return item.target.getBoundingClientRect().top > closestItem.target.getBoundingClientRect().top ? item : closestItem;
          }, null);
      }

      this.setActive((currentItem || this.items[0]).link);
    }

    setActive(link) {
      this.links.forEach((item) => {
        const isActive = item === link;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      if (!link || this.activeLink === link) return;

      this.activeLink = link;
      this.revealLink(link);
    }

    revealLink(link) {
      const trackRect = this.track.getBoundingClientRect();
      const linkRect = link.getBoundingClientRect();

      if (linkRect.left >= trackRect.left && linkRect.right <= trackRect.right) return;

      const targetLeft = this.track.scrollLeft + linkRect.left - trackRect.left - (this.track.clientWidth - link.offsetWidth) / 2;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: Math.max(0, targetLeft), behavior: reducedMotion ? 'auto' : 'smooth' });
    }

    openTargetAccordion(target) {
      const selector = '[data-product-specifications-accordion][data-open-from-anchor-nav="true"]';
      const specifications = target.matches(selector) ? target : target.querySelector(selector);
      const disclosure = specifications?.querySelector('accordion-disclosure');
      const details = disclosure?.querySelector('details');

      if (!details || details.open) return;

      if (typeof disclosure.open === 'function') {
        disclosure.open();
      } else {
        details.open = true;
        details.setAttribute('aria-expanded', 'true');
      }
    }

    handleClick(event, link) {
      const item = this.items?.find((entry) => entry.link === link);
      if (!item) return;

      event.preventDefault();
      this.setActive(link);
      this.openTargetAccordion(item.target);

      const targetTop = item.target.getBoundingClientRect().top + window.scrollY;
      const top = targetTop - this.getStickyOffset() - this.root.offsetHeight;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      history.replaceState(null, '', `#${link.dataset.anchorId}`);
      window.scrollTo({ top: Math.max(0, top), behavior: reducedMotion ? 'auto' : 'smooth' });
    }
  }

  const initialize = (scope = document) => {
    const navigations = [];

    if (scope.matches?.(navigationSelector)) navigations.push(scope);
    navigations.push(...scope.querySelectorAll(navigationSelector));

    navigations.forEach((navigation) => new ProductAnchorScrollSpy(navigation));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
